import React from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import { Redirect } from 'react-router-dom'
import { useUser, useUserPermissions } from '@paintscout/ui-business'
import DeactivatedDashboard from '../pages/dashboard/DeactivatedDashboard'
import TrialOverDashboard from '../pages/dashboard/TrialOverDashboard'
import SherwinTrialOverDashboard from '../pages/dashboard/SherwinTrialOverDashboard'
import Dashboard from '../pages/dashboard/Dashboard/Dashboard'

/**
 * Picks which dashboard `/dashboard` renders.
 *
 * Its own file rather than a function inside App.tsx, because this branch decides whether a
 * deactivated account sees the block screen and it has regressed twice — once by exempting
 * superadmins into a blank dashboard, once by keying on `isChurned` (false for the mid-term
 * cancellation Chargebee actually performs). App.tsx cannot be imported by a spec: its lazy route
 * imports pull in the whole application graph.
 */
export default function DashboardComponent(_: RouteComponentProps) {
  const { isTrial, trialExpired, isSubscribed, user } = useUser()
  const { permissionToViewDashboard } = useUserPermissions()
  const leadSource = user?.app_metadata?.company?.leadSource || ''

  // The deactivated block screen (ENG-1532), keyed on `!isSubscribed` rather than `isChurned`.
  //
  // `isChurned` is `cancelled && current_term_end < now`, and Chargebee cancels a dunning account
  // MID-TERM — so the term end is in the future, `isChurned` is false, and a non-paying account
  // fell straight through to the normal dashboard. `!isSubscribed` catches both that and the
  // ordinary end-of-term churn.
  //
  // Checked BEFORE the permissionToViewDashboard redirect: without this order a deactivated user
  // who cannot view the dashboard gets bounced to /quotes/ and never sees the block screen at all.
  //
  // Deliberately NO superadmin/support exemption here, unlike SubscriptionGuard. That guard exempts
  // internal staff so they can still reach /quotes and the rest of the app to investigate, which
  // is about NAVIGATION. This is about what /dashboard renders, and the normal dashboard on an
  // unsubscribed account renders empty — an exemption gave staff a blank page instead of the
  // screen that explains the account's state and offers Select Plan. The pre-ENG-1532 code had no
  // role check on this branch either.
  if (!isSubscribed) {
    return <DeactivatedDashboard />
  }

  if (!permissionToViewDashboard) {
    return <Redirect to="/quotes/" />
  }

  if (isTrial && trialExpired) {
    const isSherwinWilliams = leadSource?.toLowerCase().includes('sherwin')
    if (isSherwinWilliams) {
      return <SherwinTrialOverDashboard />
    } else {
      return <TrialOverDashboard />
    }
  }

  return <Dashboard />
}
